import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Card,
  Alert,
  Stack,
  Chip,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Bar } from "react-chartjs-2";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import NoEntryFilter from "../Common/NoEntryFilter";
import { fetchNoEntryIncidentsPerCamera } from "../../services/noEntryZoneApi";

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: "gray",
        usePointStyle: true,
      },
    },
    tooltip: {
      mode: "index",
      intersect: false,
    },
  },
  scales: {
    x: {
      ticks: { color: "gray" },
      grid: { display: false },
    },
    y: {
      ticks: { color: "gray" },
      beginAtZero: true,
      grid: { color: "rgba(200, 200, 200, 0.15)" },
    },
  },
};

const NoEntryIncidentsChart = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  const loadData = useCallback(async (activeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeFilter.camera_id) params.camera_id = activeFilter.camera_id;
      if (activeFilter.start_date) params.start_date = activeFilter.start_date;
      if (activeFilter.end_date) params.end_date = activeFilter.end_date;
      if (activeFilter.start_time) params.start_time = activeFilter.start_time;
      if (activeFilter.end_time) params.end_time = activeFilter.end_time;
      if (activeFilter.location) params.location = activeFilter.location;
      if (activeFilter.area) params.area = activeFilter.area;
      if (activeFilter.building) params.building = activeFilter.building;
      if (activeFilter.floor_level) params.floor_level = activeFilter.floor_level;
      if (activeFilter.zone) params.zone = activeFilter.zone;

      const data = await fetchNoEntryIncidentsPerCamera(params, token);
      const items = Array.isArray(data)
        ? data
        : data?.items || data?.incidents_by_camera || data?.data || [];
      setDataList(items);
    } catch (err) {
      console.error("Error fetching no-entry incidents per camera:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          "Unable to load incidents per camera analytics."
      );
      setDataList([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadData(filter);
    }
  }, [filter, loadData, token]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Filter No-Entry Incidents by Camera",
      content: (
        <NoEntryFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
          }}
        />
      ),
      sendReq: () => {
        childRef.current?.submit();
      },
    });
  };

  const labels = dataList.map(
    (item) => item.camera_name || item.name || `Camera ${item.stream_id?.slice(0, 6) || ""}`
  );
  const eventsCounts = dataList.map((item) => item.events ?? item.event_count ?? 0);
  const incidentsCounts = dataList.map(
    (item) => item.incidents ?? item.incident_count ?? item.count ?? 0
  );
  const resolvedCounts = dataList.map((item) => item.resolved ?? item.resolved_count ?? 0);

  const totalEvents = eventsCounts.reduce((acc, c) => acc + c, 0);
  const totalIncidents = incidentsCounts.reduce((acc, c) => acc + c, 0);
  const totalResolved = resolvedCounts.reduce((acc, c) => acc + c, 0);

  const chartData = {
    labels: labels.length > 0 ? labels : ["No Data Available"],
    datasets: [
      {
        label: "Detected",
        data: labels.length > 0 ? eventsCounts : [0],
        backgroundColor: "rgba(54, 162, 235, 0.75)",
        borderColor: "rgb(54, 162, 235)",
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Acknowledged",
        data: labels.length > 0 ? incidentsCounts : [0],
        backgroundColor: "rgba(255, 99, 132, 0.75)",
        borderColor: "rgb(255, 99, 132)",
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Resolved",
        data: labels.length > 0 ? resolvedCounts : [0],
        backgroundColor: "rgba(75, 192, 192, 0.75)",
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  return (
    <Card sx={{ p: 3, bgcolor: "background.paper" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Number of Incidents per Camera
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comparative breakdown of recorded events, detected incidents, and resolved cases across cameras.
          </Typography>
        </Box>
        <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
        </DesBtn>
      </Box>

      {/* Summary KPI chips */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1} mb={2}>
        <Chip
          label={`Cameras: ${dataList.length}`}
          variant="outlined"
          color="default"
          size="small"
        />
        <Chip
          label={`Total Detected: ${totalEvents}`}
          variant="outlined"
          color="info"
          size="small"
        />
        <Chip
          label={`Total Acknowledged: ${totalIncidents}`}
          variant="outlined"
          color="error"
          size="small"
        />
        <Chip
          label={`Total Resolved: ${totalResolved}`}
          variant="outlined"
          color="success"
          size="small"
        />
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
      ) : dataList.length === 0 && !error ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center", bgcolor: "background.default", borderRadius: 2 }}
        >
          <Typography variant="body1" color="text.secondary">
            No incident statistics available for the selected filters.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2, height: 440, bgcolor: "background.paper" }}>
          <Box sx={{ height: 390 }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        </Paper>
      )}
    </Card>
  );
};

export default NoEntryIncidentsChart;
